import {USER_KEYS} from "@/api/userApi";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import WhiteTickIcon from "@/assets/FFFFFF_tick_icon.svg?react";
import InstagramProfileLink from "@/components/Common/Influencer/InstagramProfileLink";
import Loader from "@/components/Common/kpi/Loader";
import {client} from "@/httpClient";
import RegistrationInputField from "@/pages/registerCompany/RegistrationInputField";
import useRegistrationStore from "@/store/registration";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import {useEffect, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useDebounce} from "react-use";

const InstagramField: FC<{label?: string; optional?: boolean}> = ({label, optional}) => {
  const {t} = useTranslation();
  const instagramUsername = useRegistrationStore(state => state.companyInstagramUsername);
  const isInstagramValidated = useRegistrationStore(state => state.isInstagramValidated);
  const [instagramState, setInstagramState] = useState(instagramUsername);
  useDebounce(
    () => {
      setValue("companyInstagramUsername", instagramState.toLowerCase());
    },
    1000,
    [instagramState],
  );
  const setValue = useRegistrationStore(state => state.setValue);
  const setError = useRegistrationStore(state => state.setError);

  const {mutate: validateUsername, isPending} = useMutation({
    mutationKey: USER_KEYS.mUserInstagramUsernameValidation,
    mutationFn: () =>
      client.post(USER_KEYS.mUserInstagramUsernameValidation[0], {username: instagramUsername.replaceAll("@", "")}),
    onSuccess: () => {
      setValue("isInstagramValidated", true);
      setError("companyInstagramUsername", "");
    },
    onError: (e: AxiosError<{detail: string}>) => {
      setValue("isInstagramValidated", false);
      if (e.response?.data?.detail === "This username already exists!")
        setError("companyInstagramUsername", "instagramUserAlreadyExists");
      else setError("companyInstagramUsername", "invalidInstagramUsername");
    },
  });

  useEffect(() => {
    if (instagramUsername === "") return;
    validateUsername();
  }, [instagramUsername, validateUsername]);

  return (
    <div className="flex flex-col gap-2">
      <RegistrationInputField
        label={t(label || "companyInstagramUsername")}
        placeholder={t("@refluenced")}
        storeKey={"companyInstagramUsername"}
        className="pl-10"
        value={instagramState}
        optional={optional}
        onChange={value => {
          setInstagramState(value);
          setValue("isInstagramValidated", false);
          setError("companyInstagramUsername", "");
        }}
        icon={
          <>
            <InstagramIcon className="text-primary-dark-solid absolute inset-y-0 translate-x-4 translate-y-5" />
            {isInstagramValidated ? (
              <div className={"absolute top-0 right-0 bottom-0 mr-3 flex h-full items-center justify-center p-2"}>
                <WhiteTickIcon className="border-success-light-green bg-success-herbal size-6 shrink-0 rounded-full border p-1" />
              </div>
            ) : (
              isPending && (
                <Loader
                  styles={{
                    loaderClassName: "h-[22px] w-[22px]",
                    containerClassName:
                      "absolute bottom-0 right-0 top-0 h-full pr-5 pl-1.5 flex items-center justify-center",
                  }}
                />
              )
            )}
          </>
        }
      />
      {isInstagramValidated && (
        <div className="flex gap-2 text-sm">
          <span className="text-neutral-grey-dark">{t("isThisYou")}</span>
          <InstagramProfileLink username={instagramUsername} withSmallGap className="text-sm" />
        </div>
      )}
    </div>
  );
};

export default InstagramField;
